import React from "react";
import "./Synthesizer.css";

function Dial({ startingValue }) {
  const [value, setValue] = React.useState(startingValue);
  const startYRef = React.useRef(0);
  const startValueRef = React.useRef(0);

  const min = 0;
  const max = 100;
  const sensitivity = 1; // smaller = slower, smoother

  const rotation = -135 + (value / 100) * 270;

  const onPointerDown = (e) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    startValueRef.current = value;

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = (e) => {
    const deltaY = startYRef.current - e.clientY;
    const next = startValueRef.current + deltaY * sensitivity;

    setValue(Math.min(max, Math.max(min, next)));
  };

  const onPointerUp = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  };

  return (
    <div className="dial" onPointerDown={onPointerDown}>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        readOnly
        className="dial-input"
      />
      <div
        className="dial-face"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        <div className="dial-indicator" />
      </div>
    </div>
  );
}

function RotaryDial({ options, startingIndex = 0, onChange }) {
  const [index, setIndex] = React.useState(startingIndex);

  const startY = React.useRef(0);
  const startIndex = React.useRef(0);

  const stepAngle = 270 / (options.length - 1);
  const rotation = -135 + index * stepAngle;

  const sensitivity = 0.03;

  const onPointerDown = (e) => {
    startY.current = e.clientY;
    startIndex.current = index;

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = (e) => {
    const delta = startY.current - e.clientY;
    const stepsMoved = Math.round(delta * sensitivity);

    if (stepsMoved !== 0) {
      const next = Math.max(
        0,
        Math.min(options.length - 1, startIndex.current + stepsMoved),
      );

      setIndex(next);
      onChange?.(options[next]);

      startY.current = e.clientY;
      startIndex.current = next;
    }
  };

  const onPointerUp = () => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  };

  return (
    <div className="dial" onPointerDown={onPointerDown}>
      {/* icons */}
      <div className="dial-options">
        {options.map((opt, i) => {
          const angle = -135 + i * stepAngle;
          return (
            <img
              key={opt.id}
              src={opt.icon}
              className={`dial-option ${i === index ? "active" : ""}`}
              style={{
                transform: `
                    rotate(${angle}deg)
                    translateY(-44px)
                    rotate(${-angle}deg)
                    `,
              }}
              alt=""
            />
          );
        })}
      </div>

      <div
        className="dial-face"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        <div className="dial-indicator" />
      </div>
    </div>
  );
}

const OPTIONS = [
  { id: "sine", icon: "/icons/sine.png" },
  { id: "triangle", icon: "/icons/triangle.png" },
  { id: "saw", icon: "/icons/saw.png" },
  { id: "square", icon: "/icons/square.png" },
  { id: "pulse", icon: "/icons/pulse.png" },
];

function OSCModule() {
  const [active, setActive] = React.useState(false);
  const containerRef = React.useRef(null);
  const interactingRef = React.useRef(false);

  const handlePointerDown = (e) => {
    if (e.target.closest(".interactive")) {
      interactingRef.current = true;
    }
  };

  const handlePointerUp = (e) => {
    if (interactingRef.current) {
      interactingRef.current = false;
      return;
    }

    if (!active) {
      setActive(true);
      return;
    }

    window.location.href = "/about";
  };

  React.useEffect(() => {
    const handleOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setActive(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`module ${active ? "active" : ""}`}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {active ? (
        <h3 className="hoveredModuleHeader">About Me</h3>
      ) : (
        <h3>Oscillator</h3>
      )}
      <h3>VCO</h3>
      <div className="interactive">
        <Dial startingValue={50} />
      </div>
      <div className="label">Pitch</div>
      <div className="interactive" style={{ marginTop: "25px" }}>
        <RotaryDial
          options={OPTIONS}
          startingIndex={0}
          onChange={(opt) => console.log("Selected:", opt.id)}
        />
        <div className="label" style={{ marginTop: "25px" }}>
          Wave
        </div>
      </div>
    </div>
  );
}

function FilterModule() {
  const [active, setActive] = React.useState(false);
  const containerRef = React.useRef(null);
  const interactingRef = React.useRef(false);

  const handlePointerDown = (e) => {
    if (e.target.closest(".interactive")) {
      interactingRef.current = true;
    }
  };

  const handlePointerUp = (e) => {
    if (interactingRef.current) {
      interactingRef.current = false;
      return;
    }

    if (!active) {
      setActive(true);
      return;
    }

    window.open("/music", "_top");
  };

  React.useEffect(() => {
    const handleOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setActive(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`module ${active ? "active" : ""}`}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {active ? (
        <h3 className="hoveredModuleHeader">Music</h3>
      ) : (
        <h3>Filter</h3>
      )}
      <div className="interactive">
        <Dial startingValue={100} />
      </div>
      <div className="label">Cutoff</div>
      <div className="interactive">
        <Dial startingValue={50} />
      </div>
      <div className="label">Resonance</div>
    </div>
  );
}

function EnvModule() {
  const [active, setActive] = React.useState(false);
  const containerRef = React.useRef(null);
  const interactingRef = React.useRef(false);

  const handlePointerDown = (e) => {
    if (e.target.closest(".interactive")) {
      interactingRef.current = true;
    }
  };

  const handlePointerUp = (e) => {
    if (interactingRef.current) {
      interactingRef.current = false;
      return;
    }

    if (!active) {
      setActive(true);
      return;
    }

    window.open("/Harry Kaden Resume 2026.pdf", "_blank");
  };

  return (
    <div
      ref={containerRef}
      className={`module ${active ? "active" : ""}`}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {active ? (
        <h3 className="hoveredModuleHeader">Resume</h3>
      ) : (
        <h3>Envelope</h3>
      )}
      <div className="interactive">
        <input className="slider" type="range" />
      </div>
      <div className="label">Attack</div>
      <div className="interactive">
        <input className="slider" type="range" />
      </div>
      <div className="label">Decay</div>
    </div>
  );
}

function LFOModule() {
  const [active, setActive] = React.useState(false);
  const containerRef = React.useRef(null);
  const interactingRef = React.useRef(false);

  const handlePointerDown = (e) => {
    if (e.target.closest(".interactive")) {
      interactingRef.current = true;
    }
  };

  const handlePointerUp = (e) => {
    if (interactingRef.current) {
      interactingRef.current = false;
      return;
    }

    if (!active) {
      setActive(true);
      return;
    }

    window.open("https://www.linkedin.com/in/harry-kaden-35b105236/", "_blank");
  };

  return (
    <div
      ref={containerRef}
      className={`module ${active ? "active" : ""}`}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {active ? (
        <h3 className="hoveredModuleHeader">LinkedIn</h3>
      ) : (
        <h3>LFO</h3>
      )}

      <div className="interactive">
        <Dial startingValue={50} />
      </div>
      <div className="label">Rate</div>

      <label className="toggle interactive">
        <input type="checkbox" />
        <span></span>
      </label>
      <div className="label">Sync</div>

      <div className="led on interactive"></div>
      <div className="label">Power</div>
    </div>
  );
}

function Synthesizer() {
  return (
    <div className="container">
      <div className="synth">
        <div className="synth-title">Harry Kaden</div>
        <div className="synth-subtitle">
          <span className="desktop-text">
            Hover over any of the modules below to navigate
          </span>
          <span className="mobile-text">
            Tap a module once to preview, tap again to navigate
          </span>
        </div>
        <div className="synth-modules">
          <OSCModule />
          <FilterModule />
          <EnvModule />
          <LFOModule />
        </div>
      </div>
    </div>
  );
}

export default Synthesizer;
